import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { InputAcceptType } from '../../../common/api/File.interface';
import { Attachment } from '../../../common/attachment/Attachment.interface';
import { pluralize } from '../../../common/formatter';
import InputFile from '../../../common/forms/InputFile';
import {
  TrainingSectionAttachmentProgress,
  TrainingUserAttachment
} from '../Training.interface';
import { InlineSelectOptions } from '../../../common/inline/InlineSelect';

const StyledDiv = styled.div`
  .input {
    border: none !important;

    .drag-text {
      text-align: center;
    }
  }
`;

export const trainingUserFileUploadTypes: InlineSelectOptions[] = [
  { value: '*', label: 'Any File Type' },
  {
    value: InputAcceptType.Images,
    label: 'Image (JPEG, JPG, PNG, GIF)'
  },
  {
    value: InputAcceptType.Video,
    label: 'Video (MP4, M4V, MOV)'
  },
  {
    value: InputAcceptType.PDF,
    label: 'PDF only'
  },
  {
    value: `${InputAcceptType.Documents},${InputAcceptType.Presentation}`,
    label: 'Document (PDF, MS Word, Powerpoint)'
  }
];

export interface TrainingUserFileUploadProps {
  disabled?: boolean;
  label?: string;
  description?: string;
  multiple?: boolean;
  fileType?: string;
  values?: TrainingUserAttachment[];
  progress?: TrainingSectionAttachmentProgress;
  onChange?: (values: Attachment[]) => void;
}

export const TrainingUserFileUpload: React.FC<
  TrainingUserFileUploadProps
> = props => {
  const {
    disabled,
    label,
    description,
    multiple,
    fileType,
    values,
    progress,
    onChange
  } = props;

  const dragLabel = useMemo(() => {
    const type =
      trainingUserFileUploadTypes.find(type => {
        return type.value === fileType;
      }) || trainingUserFileUploadTypes[0];

    const min = progress?.min ?? 0;
    const label = !multiple
      ? 'Drag a file here to upload'
      : min > 0
      ? `Drag at least ${min} file${pluralize(min, '', 's')} here to upload`
      : 'Drag one or more files to upload';

    return (
      <>
        {label}
        <br />
        {type.label}
      </>
    );
  }, [multiple, progress, fileType]);

  return (
    <StyledDiv className='file-upload-container'>
      {label && <Typography variant='body2'>{label}</Typography>}
      {description && <Typography variant='caption'>{description}</Typography>}
      <InputFile
        dragLabel={dragLabel}
        className='input'
        disabled={disabled}
        multiple={multiple}
        inputType={fileType && fileType !== '*' ? fileType : undefined}
        uploadPath='attachments'
        values={values}
        onChange={onChange}
      />
      {progress && progress.min > 0 && (
        <Alert
          style={{ marginTop: 5 }}
          severity={progress.isValid ? 'success' : 'warning'}
        >
          Uploaded <strong>{progress.total}</strong> of{' '}
          <strong>{progress.min}</strong> required{' '}
          {pluralize(progress.min, 'file', 'files')}.
        </Alert>
      )}
    </StyledDiv>
  );
};
